import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useSEO } from '../hooks/useSEO';
import { Sparkles, Copy, Check, Loader2, FileText, Target, Package, Users, Megaphone, Calendar, DollarSign, AlertTriangle, ListChecks, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIBlueprint() {
  useSEO('aiBlueprint');
  
  const [form, setForm] = useState({
    product_type: '',
    sub_category: '',
    target_retail_price: '',
    market: '',
    brand_positioning: '',
    target_customer: '',
    packaging_format: '',
    flavor_profile: '',
    sales_channels: '',
    additional_notes: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = useMemo(() => {
    return form.product_type.trim() && form.target_retail_price.trim() && form.market.trim();
  }, [form]);

  function updateField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function generateBlueprint() {
    if (!canSubmit) return;

    setLoading(true);
    setResult('');
    setError('');
    setCopied(false);

    try {
      const response = await fetch(`${API_URL}/api/blueprint/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to generate blueprint.');
      }

      setResult(data.blueprint || '');
    } catch (err) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  async function copyResult() {
    if (!result) return;
    await navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const expectedSections = [
    { icon: FileText, title: '1. Executive Summary' },
    { icon: Sparkles, title: '2. Formula Direction' },
    { icon: Package, title: '3. Packaging Strategy' },
    { icon: Target, title: '4. Label & Design Direction' },
    { icon: Users, title: '5. Co-Packer Profile' },
    { icon: Target, title: '6. Distributor Strategy' },
    { icon: Megaphone, title: '7. Marketing Blueprint' },
    { icon: Calendar, title: '8. 90-Day Launch Plan' },
    { icon: DollarSign, title: '9. Budget Framework' },
    { icon: AlertTriangle, title: '10. Risks & Bottlenecks' },
    { icon: ListChecks, title: '11. Next 10 Action Steps' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy via-navy to-slate-900 py-12 md:py-16" data-testid="ai-blueprint-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-copper/20 text-copper text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              AI-Powered Tool
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Beverage Company Blueprint Builder
            </h1>
            <p className="text-white/80 text-lg">
              Generate a complete launch blueprint with formula direction, packaging strategy, 
              co-packer targets, distributor approach, marketing plan, and 90-day action steps.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-slate-50" data-testid="ai-blueprint-form">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Form Panel */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
              <h2 className="font-heading text-xl font-semibold text-navy mb-6">
                Describe Your Beverage Concept
              </h2>
              
              <div className="space-y-5">
                <InputField
                  label="Product Type"
                  required
                  placeholder="Tequila, RTD Cocktail, Wine, Sparkling Water, Functional Beverage"
                  value={form.product_type}
                  onChange={(v) => updateField('product_type', v)}
                  testId="product-type-input"
                />
                
                <InputField
                  label="Sub-Category"
                  placeholder="Blanco tequila, canned mai tai, luxury rosé, adaptogen sparkling water"
                  value={form.sub_category}
                  onChange={(v) => updateField('sub_category', v)}
                  testId="sub-category-input"
                />
                
                <InputField
                  label="Target Retail Price"
                  required
                  placeholder="$49.99, $14.99 4-pack, $79.99"
                  value={form.target_retail_price}
                  onChange={(v) => updateField('target_retail_price', v)}
                  testId="price-input"
                />
                
                <InputField
                  label="Primary Market"
                  required
                  placeholder="Hawaii, California, Nevada, National"
                  value={form.market}
                  onChange={(v) => updateField('market', v)}
                  testId="market-input"
                />
                
                <InputField
                  label="Brand Positioning"
                  placeholder="Luxury, wellness, farm-to-table, rebellious, heritage"
                  value={form.brand_positioning}
                  onChange={(v) => updateField('brand_positioning', v)}
                  testId="positioning-input"
                />
                
                <InputField
                  label="Target Customer"
                  placeholder="High-end on-premise, resort guests, health-conscious women 30-50"
                  value={form.target_customer}
                  onChange={(v) => updateField('target_customer', v)}
                  testId="customer-input"
                />
                
                <InputField
                  label="Packaging Format"
                  placeholder="750ml glass, 355ml slim can, 12oz PET, 6-pack case"
                  value={form.packaging_format}
                  onChange={(v) => updateField('packaging_format', v)}
                  testId="packaging-input"
                />
                
                <InputField
                  label="Flavor Profile"
                  placeholder="Yuzu-forward, tamarind spice, crisp minerality, vanilla oak"
                  value={form.flavor_profile}
                  onChange={(v) => updateField('flavor_profile', v)}
                  testId="flavor-input"
                />
                
                <InputField
                  label="Sales Channels"
                  placeholder="On-premise, off-premise, DTC where legal, hotels, golf courses"
                  value={form.sales_channels}
                  onChange={(v) => updateField('sales_channels', v)}
                  testId="channels-input"
                />
                
                <TextAreaField
                  label="Additional Notes"
                  placeholder="Any special constraints, ingredients, bottle ideas, margin requirements, investor angle..."
                  value={form.additional_notes}
                  onChange={(v) => updateField('additional_notes', v)}
                  testId="notes-input"
                />

                {error && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  onClick={generateBlueprint}
                  disabled={!canSubmit || loading}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                    !canSubmit || loading 
                      ? 'bg-slate-300 cursor-not-allowed' 
                      : 'bg-copper hover:bg-copper/90'
                  }`}
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Building Blueprint...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Beverage Blueprint
                    </>
                  )}
                </button>
                
                <p className="text-xs text-slate-500 text-center">
                  Requires Brand Builder Access ($199) or Coalition Membership
                </p>
              </div>
            </div>

            {/* Result Panel */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Output</div>
                  <h2 className="font-heading text-xl font-semibold text-navy">
                    AI Company Blueprint
                  </h2>
                </div>
                <button
                  onClick={copyResult}
                  disabled={!result}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    result 
                      ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' 
                      : 'bg-slate-50 text-slate-400 cursor-not-allowed'
                  }`}
                  data-testid="copy-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              
              <div className="flex-1 p-6 overflow-auto max-h-[700px]">
                {result ? (
                  <div 
                    className="prose prose-slate prose-sm max-w-none whitespace-pre-wrap"
                    data-testid="blueprint-result"
                  >
                    {result}
                  </div>
                ) : (
                  <div className="text-slate-500">
                    <p className="mb-6">Your AI-generated blueprint will appear here.</p>
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-600">Expected sections:</p>
                      {expectedSections.map((section, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-sm">
                          <section.icon className="w-4 h-4 text-copper" />
                          <span>{section.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="mt-12 bg-navy rounded-2xl p-8 text-center">
            <h3 className="font-heading text-2xl font-semibold text-white mb-3">
              Ready to Launch Your Beverage Brand?
            </h3>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Get full access to AI Blueprint Builder, Brand Builder tool, and connect with our supplier network.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/checkout?package=brand_builder"
                className="btn-copper"
                data-testid="buy-brand-builder-btn"
              >
                Get Brand Builder Access — $199
              </Link>
              <Link
                to="/request-access"
                className="px-6 py-3 rounded-xl font-semibold text-white border border-white/20 hover:bg-white/10 transition-colors"
              >
                Join Coalition
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function InputField({ label, value, onChange, placeholder, required = false, testId }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-navy">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper transition-all"
        data-testid={testId}
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-navy">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper transition-all resize-vertical"
        data-testid={testId}
      />
    </label>
  );
}
